/**
 * Portal-managed email templates for New Western applicant communications.
 *
 * These templates cover EVERY email a New Western applicant can receive.
 * They are a New Western–only concept: Left Main submissions never email
 * applicants, and nothing here is referenced by any Left Main code path.
 *
 * Template bodies are plain text — blank lines separate paragraphs, and
 * {{placeholder}} tokens are substituted at send time. The branded HTML
 * shell (header, footer, tracking-ID box) is fixed in code; only the copy
 * is editable via the portal.
 */

export const NW_EMAIL_TEMPLATE_KINDS = [
  'thank_you',
  'decision_approved',
  'decision_rejected',
  'draw_processed',
] as const;

export type NwEmailTemplateKind = (typeof NW_EMAIL_TEMPLATE_KINDS)[number];

/** Placeholders available in every template's subject and body. */
export const NW_TEMPLATE_PLACEHOLDERS = [
  'first_name',
  'full_name',
  'entity_name',
  'tracking_id',
] as const;

export type NwTemplatePlaceholder = (typeof NW_TEMPLATE_PLACEHOLDERS)[number];

export const NW_EMAIL_TEMPLATE_LABELS: Record<NwEmailTemplateKind, string> = {
  thank_you: 'Thank You (application received)',
  decision_approved: 'Decision — Approved',
  decision_rejected: 'Decision — Rejected',
  draw_processed: 'Draw Processed',
};

export interface NwEmailTemplate {
  kind: NwEmailTemplateKind;
  subject: string;
  /** Large headline at the top of the email body. Empty string = no headline. */
  heading: string;
  body: string;
  /** false = built-in default copy; true = overridden via the portal. */
  is_customized: boolean;
  updated_at?: string;
  updated_by?: string;
}

/** Extracts {{placeholder}} token names from a template string. */
export function extractTemplatePlaceholders(text: string): string[] {
  const tokens = new Set<string>();
  for (const match of text.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)) {
    tokens.add(match[1]);
  }
  return [...tokens];
}

/** Returns the tokens in `text` that are not valid placeholders. */
export function findInvalidPlaceholders(text: string): string[] {
  const allowed = new Set<string>(NW_TEMPLATE_PLACEHOLDERS);
  return extractTemplatePlaceholders(text).filter((t) => !allowed.has(t));
}
